import { BaseApi, HttpMethodEnum } from '../BaseApi/BaseApi';
import { 
    IBillingReport, 
    IBillingReportFilter, 
    BillingReport, 
    IMarkBilledRequest,
    IMarkBilledResponse,
    MarkBilledResponse
} from '../../entities/TestKitOrder/BillingTypes';

/**
 * BillingApi - API service for BL-002 Admin Billing Reports Feature
 * Provides methods to interact with billing endpoints
 */
export class BillingApi extends BaseApi {
    private static baseApiUrl: string = `${BaseApi.apiUrl}/TestKitOrders`;

    public static apiUrls = {
        getBillingReport: `${BillingApi.baseApiUrl}/BillingReport`,
        markOrdersAsBilled: `${BillingApi.baseApiUrl}/MarkAsBilled`,
    };

    /**
     * Get billing report with filtering and pagination
     * Maps to GET /TestKitOrders/BillingReport endpoint
     */
    public static async getBillingReport(filter: IBillingReportFilter): Promise<BillingReport> {
        return this.sendApiRequest<IBillingReport>({
            method: HttpMethodEnum.POST, // Using POST to send complex filter object
            path: this.apiUrls.getBillingReport,
            data: filter as any,
        }).then(response => new BillingReport(response));
    }

    /**
     * Mark orders as billed in QuickBooks
     * Maps to POST /TestKitOrders/MarkAsBilled endpoint
     */
    public static async markOrdersAsBilled(request: IMarkBilledRequest): Promise<MarkBilledResponse> {
        return this.sendApiRequest<IMarkBilledResponse>({
            method: HttpMethodEnum.POST,
            path: this.apiUrls.markOrdersAsBilled,
            data: request as any,
        }).then(response => new MarkBilledResponse(response));
    }

    /**
     * Helper method to get unbilled orders only
     * Convenience method with pre-configured filter
     */
    public static async getUnbilledOrders(pageNumber: number = 1, pageSize: number = 25): Promise<BillingReport> {
        const filter: IBillingReportFilter = {
            statusFilter: 1, // 1=NotBilled
            pageNumber,
            pageSize,
            sortBy: 'CreatedDate',
            sortDirection: 'DESC',
        };

        return this.getBillingReport(filter);
    }

    /**
     * Helper method to get all orders (billed and unbilled)
     * Convenience method with pre-configured filter
     */
    public static async getAllOrders(pageNumber: number = 1, pageSize: number = 25): Promise<BillingReport> {
        const filter: IBillingReportFilter = {
            statusFilter: 2, // 2=All
            pageNumber,
            pageSize,
            sortBy: 'CreatedDate',
            sortDirection: 'DESC',
        };

        return this.getBillingReport(filter);
    }

    /**
     * Helper method to get billed orders only
     * Convenience method with pre-configured filter
     */
    public static async getBilledOrders(pageNumber: number = 1, pageSize: number = 25): Promise<BillingReport> {
        const filter: IBillingReportFilter = {
            statusFilter: 3, // 3=BilledOnly
            pageNumber,
            pageSize,
            sortBy: 'CreatedDate',
            sortDirection: 'DESC',
        };

        return this.getBillingReport(filter);
    }
} 